<template>
    <div style="position: relative;">
        <div class="refresh">
            <el-button-group>
                <el-button @click="refresh(true)" icon="Refresh" :loading="loading"></el-button>
                <el-popover placement="top" :width="60" popper-class="custom-popover">
                    <div>
                        <el-button text @click="reload()">重新载入</el-button>
                    </div>
                    <div>
                        <el-button text @click="showLog()">显示日志</el-button>
                    </div>
                    <template #reference>
                        <el-button icon="Setting"></el-button>
                    </template>
                </el-popover>
            </el-button-group>
        </div>
        <el-table :data="list" empty-text="暂无仓位" v-loading="loading">
            <el-table-column label="交易对" align="center" width="120">
                <template #default="{ row }">
                    {{ row.instId.replace("-SWAP", "").replace("-", "") }}
                </template>
            </el-table-column>
            <el-table-column label="仓位模式" align="center" width="120">
                <template #default="{ row }">
                    <el-tag v-show="row.mgnMode === constants.CROSS"
                        :type="row.posSide === constants.SHORT ? 'danger' : 'success'"
                        style="width:80px; text-align:center">全仓 × {{
                            row.lever }}</el-tag>
                    <el-tag v-show="row.mgnMode !== constants.CROSS"
                        :type="row.posSide === constants.SHORT ? 'danger' : 'success'"
                        style="width:80px; text-align:center">逐仓 × {{
                            row.lever }}</el-tag>
                </template>
            </el-table-column>
            <el-table-column label="开仓均价" prop="avgPx1" align="center" width="100"></el-table-column>
            <el-table-column label="数量" prop="pos" align="center" width="100"></el-table-column>
            <el-table-column label="保证金" align="center" width="100">
                <template #default="{ row }">
                    <span v-if="row.mgnMode === constants.CROSS">
                        {{ new Decimal(row.imr).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber() }}
                    </span>
                    <span v-else>
                        {{ new Decimal(row.margin).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber() }}
                    </span>
                </template>
            </el-table-column>
            <el-table-column label="参考强平价" prop="liqPx1" align="center" width="100"></el-table-column>
            <el-table-column label="标记价格" prop="markPx1" align="center" width="130"></el-table-column>
            <el-table-column label="收益额" align="center" width="100">
                <template #default="{ row }">
                    <span :class="row.upl < 0 ? 'red' : 'green'">
                        {{ isFinite(row.upl) && row.upl !== null ? new Decimal(row.upl).toDecimalPlaces(2,
                            Decimal.ROUND_HALF_UP).toNumber() : 0 }}
                    </span>
                </template>
            </el-table-column>
            <el-table-column label="收益率" align="center" width="100">
                <template #default="{ row }">
                    <span :class="row.upl < 0 ? 'red' : 'green'">
                        {{ isFinite(row.upl) && row.upl !== null ? new Decimal(parseFloat(row.uplRatio) *
                            100).toDecimalPlaces(2,
                                Decimal.ROUND_HALF_UP).toNumber() : 0 }}%
                    </span>
                </template>
            </el-table-column>
            <el-table-column label="" width="60" align="center">
                <template #default="{ row }">
                    <el-button plain @click="closePosition(row)" size="small">平仓</el-button>
                </template>
            </el-table-column>
            <el-table-column label="" width="50" align="center">
                <template #default="{ row }">
                    <CancelOrder :row="row" />
                </template>
            </el-table-column>
            <el-table-column label="">
                <template #default="{ row }">
                    <el-tag type="info" class="stopprofit" @click="takeStopProfit(row)">
                        <span class="green"> {{ (row.closeOrderAlgo.length && Number(row.closeOrderAlgo[0].tpTriggerPx))
                            || '--'
                        }}</span>&nbsp;
                        <span class="red"> {{ (row.closeOrderAlgo.length && Number(row.closeOrderAlgo[0].slTriggerPx))
                            || '--'
                        }}</span>
                    </el-tag>
                </template>
            </el-table-column>
        </el-table>
        <TakeStopProfit ref="TakeStopProfitRef" />
        <ClosePosition ref="ClosePositionRef" />
    </div>
</template>

<style scoped>
.refresh {
    position: absolute;
    top: -50px;
    right: 10px;
    height: 30px;
    z-index: 100;
}

.stopprofit {
    min-width: 65px;
    text-align: center;
    cursor: pointer;
}

.green {
    color: #67c23a;
}

.red {
    color: #ff707e;
}
</style>

<script setup>
import { ref, toRaw, inject, onMounted, onUnmounted } from "vue"
import { ElMessage } from 'element-plus'
import { useSystemStore } from '@/stores/system'
import { useLogStore } from '@/stores/log'
import * as constants from '@/utils/okxConstants'
import Decimal from 'decimal.js'
import CancelOrder from "@/components/crypto/okx/positions/CancelOrder"
import CryptoJS from "crypto-js"
import TakeStopProfit from "@/components/crypto/okx/positions/TakeStopProfit"
import ClosePosition from "@/components/crypto/okx/positions/ClosePosition"
import WebSocketManager from "@/utils/okxWebSocket"

const pannelActions = inject("pannelActions")
const systemStore = useSystemStore()
const logStore = useLogStore()
const loading = ref(false)
const list = ref([])
const TakeStopProfitRef = ref(null)
const ClosePositionRef = ref(null)

let positionWS = null
let markPriceWS = null
let subscribedInstIds = new Set()

onUnmounted(() => {
    markPriceWS?.close()
    positionWS?.close()
})

onMounted(() => {
    initWebSocket()
})

const initWebSocket = () => {
    // ========================
    // 公共 WS（mark price）
    // ========================
    markPriceWS = new WebSocketManager("wss://ws.okx.com:8443/ws/v5/public")
    markPriceWS.on("onMessage", (message) => {
        try {
            // ✅ 过滤 pong
            if (message === "pong") return

            const data = JSON.parse(message)

            if (data.arg?.channel !== "mark-price") return

            const ticker = data.data?.[0]
            if (!ticker) return

            updateMarkPrice(ticker)
        } catch (error) {

        }
    })
    markPriceWS.connect()

    // ========================
    // 私有 WS（持仓）
    // ========================
    positionWS = new WebSocketManager("wss://ws.okx.com:8443/ws/v5/private")

    // ✅ 注入 login（关键）
    positionWS.setLogin(async (ws) => {
        const { timestamp, sign } = generateOKXSign()

        ws.send({
            op: "login",
            args: [{
                apiKey: systemStore.api.apiKey,
                passphrase: systemStore.api.passphrase,
                timestamp,
                sign
            }]
        })
    })

    positionWS.on("onOpen", () => {
        ElMessage.success("WebSocket 已连接")
    })

    positionWS.on("onMessage", (message) => {

        // ✅ 过滤 pong
        if (message === "pong") return

        const data = JSON.parse(message)

        // ✅ 登录成功后订阅
        if (data.event === "login" && data.code === "0") {
            console.log("✅ WebSocket 登录成功")

            positionWS.subscribe({
                op: "subscribe",
                args: [{ channel: "positions", instType: "SWAP" }]
            })

            return
        }

        if (data.arg?.channel === "positions") {
            handlePositions(data.data)
        }
    })

    positionWS.on("onClose", () => {
        console.log("❌ WebSocket 连接关闭")
    })

    positionWS.on("onError", (error) => {
        ElMessage.error("WebSocket 错误：" + error)
        console.error('WebSocket error:', error)
    })

    positionWS.connect()
}

const subscribeHandler = (instIds) => {
    const toSubscribe = instIds.filter(id => !subscribedInstIds.has(id))
    const toUnsubscribe = [...subscribedInstIds].filter(id => !instIds.includes(id))

    // ✅ 单个订阅（核心）
    toSubscribe.forEach(id => {
        markPriceWS.subscribe({
            op: "subscribe",
            args: [{ channel: "mark-price", instId: id }]
        })
        subscribedInstIds.add(id)
    })

    // ✅ 单个取消
    toUnsubscribe.forEach(id => {
        markPriceWS.unsubscribe({
            op: "unsubscribe",
            args: [{ channel: "mark-price", instId: id }]
        })
        subscribedInstIds.delete(id)
    })
}

const handlePositions = (data) => {
    if (!Array.isArray(data)) return

    if (data.length === 0) {
        list.value = []
        return
    }

    //处理订阅信息
    subscribeHandler(data.map(pos => pos.instId))

    const _list = data.filter(e => e.pos !== "0")
    const updatedData = _list.map(e => {
        let s = systemStore.usymbols.find(x => x.symbol === e.instId)
        const decimalPlaces = s?.price?.toString().split('.')[1]?.length || 0
        e.avgPx1 = e.avgPx ? new Decimal(e.avgPx).toDecimalPlaces(decimalPlaces, Decimal.ROUND_DOWN).toNumber() : 0
        e.liqPx1 = e.liqPx ? new Decimal(e.liqPx).toDecimalPlaces(decimalPlaces, Decimal.ROUND_DOWN).toNumber() : 0
        e.markPx1 = e.markPx ? new Decimal(e.markPx).toDecimalPlaces(decimalPlaces, Decimal.ROUND_DOWN).toNumber() : 0

        e.imr = isNaN(e.imr) || e.imr === null ? 0 : Number(e.imr)
        e.margin = isNaN(e.margin) || e.margin === null ? 0 : Number(e.margin)
        e.upl = isNaN(e.upl) || e.upl === null ? 0 : Number(e.upl)
        e.uplRatio = isNaN(e.uplRatio) || e.uplRatio === null ? 0 : Number(e.uplRatio)

        //显示止盈和止损
        if (Array.isArray(e.closeOrderAlgo) && e.closeOrderAlgo.length) {
            let algo = e.closeOrderAlgo[0]
            e.algoId = algo.algoId
            e.tpTriggerPx = Number(algo.tpTriggerPx)
            e.slTriggerPx = Number(algo.slTriggerPx)
        } else {
            e.algoId = ""
            e.tpTriggerPx = 0
            e.slTriggerPx = 0
        }
        return e
    })

    list.value.splice(0, list.value.length, ...updatedData)
}

const updateMarkPrice = (ticker) => {
    const instId = ticker.instId
    const markPx = new Decimal(ticker.markPx)

    const targets = list.value.filter(item => item.instId === instId)

    targets.forEach(target => {
        let symbol = systemStore.usymbols.find(x => x.symbol === instId)
        const decimalPlaces = symbol?.price?.toString().split('.')[1]?.length || 0

        let ctVal = new Decimal(symbol.info.ctVal)     // 合约面值
        let avgPx = new Decimal(target.avgPx)  // 开仓均价
        let pos = new Decimal(target.pos)  // 张数

        // 成交量 = 张数 * 价格 * 合约面值，使用高精度计算
        let quantity = ctVal.times(avgPx).times(pos)
        let upl = new Decimal(0)
        if (target.posSide === constants.SHORT) {
            // 做空: 收益 = 成交量 * (开仓价 - 标记价) / 开仓价
            upl = quantity.times(avgPx.minus(markPx)).dividedBy(avgPx)
        } else {
            // 做多: 收益 = 成交量 * (标记价 - 开仓价) / 开仓价
            upl = quantity.times(markPx.minus(avgPx)).dividedBy(avgPx)
        }
        target.upl = upl.toNumber()
        target.markPx1 = markPx.toDecimalPlaces(decimalPlaces, Decimal.ROUND_DOWN).toNumber()
    })

}

const refresh = async (isload) => {
    if (isload) loading.value = true
    await getPositions()
    if (isload) loading.value = false
}

const reload = () => {
    pannelActions.reloadPositions()
}

const showLog = () => {
    pannelActions.showLog()
}

const getPositions = async () => {
    try {
        let result = await systemStore.privateClient.getPositions()
        result.forEach(e => {
            let s = systemStore.usymbols.find(x => x.symbol === e.instId)
            const decimalPlaces = s?.price?.toString().split('.')[1]?.length || 0
            e.avgPx1 = e.avgPx ? new Decimal(e.avgPx).toDecimalPlaces(decimalPlaces, Decimal.ROUND_DOWN).toNumber() : 0
            e.liqPx1 = e.liqPx ? new Decimal(e.liqPx).toDecimalPlaces(decimalPlaces, Decimal.ROUND_DOWN).toNumber() : 0
            e.markPx1 = e.markPx ? new Decimal(e.markPx).toDecimalPlaces(decimalPlaces, Decimal.ROUND_DOWN).toNumber() : 0
        })
        list.value = result
    } catch (error) {
        ElMessage.error('获取仓位失败: ' + error.message)
        logStore.addLog({
            title: '获取仓位失败',
            level: 'error',
            message: error
        })
    }
}

const generateOKXSign = () => {
    console.log(systemStore.api.secret)
    const timestamp = Math.floor(Date.now() / 1000).toString()
    const message = timestamp + "GET" + "/users/self/verify"
    const sign = CryptoJS.HmacSHA256(message, systemStore.api.secret).toString(CryptoJS.enc.Base64)
    return { timestamp, sign }
}

const closePosition = (data) => {
    ClosePositionRef.value.init(data)
}

const takeStopProfit = (data) => {
    TakeStopProfitRef.value.init(toRaw(data))
}


</script>